/**
 * nombreCaja.ts — caja «frase con nombres propios» (RAE) para el `nombre` de P1.
 *
 * NOMRAE-2 (24-sep-2026). El extractor entregaba el nombre con la caja del
 * documento: 865 de 2 368 filas del corpus en versales, 1 454 en caja mixta.
 * Aquí se pasa a frase: inicial mayúscula, resto en minúscula, respetando siglas,
 * propios (topónimos, santoral, nombres, instituciones, marcas) y acentos.
 *
 * Regla sellada por Luis (DOP-2 c′): dos diccionarios. Lo que está en COMUNES
 * baja a minúscula (con su acento); lo que está en PROPIOS se capitaliza; lo
 * desconocido conserva la inicial si tiene ≥ 5 letras (medido en el corpus:
 * cuatro de cada cinco desconocidos son pedanías, apellidos o marcas) y baja si
 * tiene ≤ 4 (DOP-3). Un nombre en caja mixta recibe la inicial y, desde CAJATIT
 * (1-oct-2026), vacías y artículos tras preposición en minúscula y siglas en versales. Los identificadores de plantilla
 * (`ER_RPEE_TBL_RINUN`, `[INICIO_TABLA]`) se devuelven intactos.
 */
import { MUNICIPIOS_POR_CODIGO, getCodigoINE, getMunicipioPorCodigo } from './codigosINEUnificado';

// ─── Utilidades ──────────────────────────────────────────────────────────────

const LETRAS = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/;
const TOKEN = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+(?:[.'’][A-Za-zÁÉÍÓÚÜÑáéíóúüñ]*)+\.?|[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+/g;

/** Clave de diccionario: mayúsculas sin tildes (los ODT las pierden). */
function clave(token: string): string {
  return token.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

function capitalizar(palabra: string): string {
  return palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase();
}

function esIdentificador(nombre: string): boolean {
  const t = nombre.trim();
  return t.startsWith('[') || (!/\s/.test(t) && t.includes('_'));
}

function esVersales(nombre: string): boolean {
  const letras = nombre.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, '');
  return letras.length > 0 && letras === letras.toUpperCase();
}

// ─── Diccionarios ────────────────────────────────────────────────────────────

/** Palabras vacías: minúscula salvo en cabeza de frase. */
const VACIAS = new Set(['DE', 'DEL', 'LA', 'EL', 'LOS', 'LAS', 'Y', 'E', 'O', 'U', 'A', 'AL', 'EN', 'CON', 'POR', 'PARA', 'SIN', 'SOBRE', 'ENTRE']);

/** Siglas sin puntos que se conservan en versales (DOP-3). */
const SIGLAS = new Set([
  'IES', 'CEIP', 'CEPER', 'CEI', 'CPR', 'CRA', 'CEE', 'CP', 'EI', 'ESO', 'FP', 'AMPA',
  'CT', 'AT', 'BT', 'MT', 'KV', 'EDAR', 'ETAP', 'EDAM', 'EBAR', 'ERAR', 'GLP', 'GNL',
  'SAS', 'GC', 'UME', 'CHARE', 'UCI', 'PAC', 'ONG', 'CECOPAL', 'CECOP',
  'SL', 'SA', 'SAU', 'SLU', 'SLL', 'SCA', 'SAT', 'CB', 'AIE', 'UTE', 'HNOS',
  'AVE', 'ADIF', 'RENFE', 'UNED', 'UGR', 'UAL', 'UJA', 'UCO', 'UMA', 'UCA', 'UHU', 'UPO', 'CSIC', 'IFAPA',
  'CD', 'CF', 'UD', 'AD', 'SD', 'RC', 'AVV', 'NNSS', 'PGOU', 'ZEC', 'LIC', 'ZEPA', 'BIC',
]);

/** Comunes PTEL con su acento (clave sin tilde → forma en minúscula). */
const COMUNES_LISTA = [
  'consultorio', 'hospital', 'ambulatorio', 'clínica', 'centro', 'salud', 'urgencias', 'farmacia', 'tanatorio', 'velatorio',
  'residencia', 'ancianos', 'mayores', 'tercera', 'edad', 'discapacitados', 'dependientes', 'asistidos', 'pensionista', 'pensionistas', 'hogar',
  'colegio', 'instituto', 'escuela', 'guardería', 'infantil', 'primaria', 'secundaria', 'universidad', 'facultad', 'conservatorio', 'educación', 'educativo', 'educativa',
  'biblioteca', 'museo', 'teatro', 'auditorio', 'cultural', 'archivo', 'sala', 'exposiciones', 'casa', 'cultura', 'ermita', 'iglesia', 'parroquia', 'parroquial', 'capilla', 'convento',
  'polideportivo', 'polideportiva', 'piscina', 'estadio', 'pabellón', 'campo', 'fútbol', 'tenis', 'pádel', 'gimnasio', 'pista', 'pistas', 'atletismo', 'deportivo', 'deportiva', 'deportivas',
  'plaza', 'parque', 'jardín', 'jardines', 'paseo', 'avenida', 'calle', 'camino', 'carretera', 'glorieta', 'rotonda', 'mirador', 'polígono', 'zona', 'área', 'recinto', 'recreativa', 'recreativo', 'feria', 'ferial',
  'municipal', 'municipales', 'cementerio', 'mercado', 'lonja', 'matadero', 'depósito', 'depósitos', 'almacén', 'taller', 'nave', 'naves', 'oficina', 'oficinas', 'sede', 'edificio', 'consistorial',
  'cuartel', 'bomberos', 'emergencias', 'emergencia', 'seguridad', 'estación', 'autobuses', 'autobús', 'tren', 'ferrocarril', 'taxi', 'parada', 'apeadero', 'aparcamiento', 'helipuerto',
  'transformador', 'transformación', 'eléctrica', 'eléctricas', 'eléctrico', 'agua', 'aguas', 'potable', 'gasolinera', 'gasoducto', 'antena', 'repetidor', 'subestación', 'central', 'línea', 'gas', 'depuradora', 'potabilizadora', 'bombeo', 'balsa', 'balsas', 'regantes', 'comunidad', 'embalse', 'presa', 'pantano', 'acequia', 'alberca', 'pozo', 'fuente',
  'panadería', 'carnicería', 'ferretería', 'instalaciones', 'instalación', 'supermercado', 'tienda', 'comestibles', 'bar', 'café', 'cafetería', 'restaurante', 'mesón', 'hostal', 'hotel', 'alojamiento', 'rural', 'turístico', 'turística', 'complejo', 'albergue', 'camping',
  'fábrica', 'industrial', 'industrias', 'industria', 'mármol', 'mármoles', 'áridos', 'granitos', 'artesanía', 'artesanos', 'carpintería', 'metálica', 'construcciones', 'transportes', 'transporte', 'logística', 'carburantes', 'automoción', 'mecánico', 'mecánica', 'explotación', 'granja', 'cooperativa', 'almazara', 'bodega', 'molino', 'cortijo', 'vivienda', 'viviendas',
  'público', 'pública', 'privado', 'privada', 'principal', 'general', 'nuevo', 'nueva', 'viejo', 'vieja', 'antiguo', 'antigua', 'médico', 'médica', 'comarcal', 'provincial', 'regional', 'nacional', 'social', 'cívico', 'usos', 'múltiples', 'salón', 'atención', 'servicios', 'servicio', 'sector', 'químico', 'alto', 'alta', 'bajo', 'baja', 'medio', 'castillo', 'torre', 'piedra', 'lugar', 'sierra', 'valle', 'río', 'monte', 'cerro', 'llano', 'vega', 'cruz', 'puente', 'cuevas', 'cueva', 'solar', 'fotovoltaica', 'eólico', 'planta', 'club', 'asociación', 'hermanos', 'hermanas', 'doctor', 'doctora', 'alcalde', 'aforo', 'tío', 'tía', 'pueblo', 'barrio', 'urbanización', 'cortijada', 'aldea', 'pedanía', 'anejo', 'policía', 'guardia', 'protección', 'socio', 'sociocultural', 'arquitectura', 'chimeneas', 'lavados', 'auxiliar', 'adultos', 'vertedero', 'clausurado', 'anfiteatro', 'frontón', 'exposiciones', 'infantil', 'junta', 'local', 'civil', 'gobierno', 'vecinal', 'sanitario', 'sanitaria', 'religioso', 'religiosa', 'juvenil', 'mujer', 'mujeres', 'jubilados', 'día', 'noche', 'norte', 'sur', 'este', 'oeste',
];
const COMUNES = new Map<string, string>(COMUNES_LISTA.map(p => [clave(p), p]));

/** Propios: forma correcta por clave (provincias, nombres, apellidos, instituciones, marcas). */
const PROPIOS_LISTA = [
  'Almería', 'Cádiz', 'Córdoba', 'Granada', 'Huelva', 'Jaén', 'Málaga', 'Sevilla', 'Andalucía', 'España',
  'San', 'Santa', 'Santo', 'Santos', 'Virgen', 'Cristo', 'Nuestra', 'Señora', 'Señor', 'Jesús', 'Anunciación', 'Asunción', 'Piedad', 'Carmen', 'Rosario', 'Dolores', 'Mercedes', 'Pilar', 'Ángeles', 'Remedios', 'Milagros', 'Concepción', 'Encarnación', 'Inmaculada',
  'José', 'Juan', 'Pedro', 'Pablo', 'María', 'Francisco', 'Antonio', 'Manuel', 'Miguel', 'Rafael', 'Gabriel', 'Daniel', 'Ismael', 'Joaquín', 'Fernando', 'Alfonso', 'Andrés', 'Emilio', 'Ricardo', 'Federico', 'Agapito', 'Otilia', 'Inocencia', 'Nuria', 'Manolita', 'Leo',
  'García', 'López', 'Martínez', 'Sánchez', 'Fernández', 'González', 'Rodríguez', 'Pérez', 'Gómez', 'Jiménez', 'Ruiz', 'Hernández', 'Díaz', 'Moreno', 'Muñoz', 'Álvarez', 'Romero', 'Alonso', 'Águila', 'Gutiérrez', 'Navarro', 'Torres', 'Domínguez', 'Vázquez', 'Ramos', 'Gil', 'Ramírez', 'Serrano', 'Blanco', 'Molina', 'Morales', 'Ortega', 'Delgado', 'Castro', 'Ortiz', 'Rubio', 'Marín', 'Sanz', 'Iglesias', 'Medina', 'Cortés', 'Garrido', 'Lorca',
  'Ayuntamiento', 'Consejería', 'Diputación', 'Correos', 'Ministerio', 'Infoca', 'Guadalinfo',
  'Endesa', 'Sevillana', 'Iberdrola', 'Naturgy', 'Repsol', 'Cepsa', 'Telefónica', 'Movistar', 'Vodafone', 'Orange', 'Mercadona', 'Carrefour', 'Lidl', 'Aldi', 'Covirán', 'Spar', 'Cosentino', 'Emasagra', 'Emproacsa', 'Aqualia', 'Hidralia', 'Giahsa', 'Emacsa', 'Emasesa', 'Aljarafesa', 'Galasa',
];
const PROPIOS = new Map<string, string>(PROPIOS_LISTA.map(p => [clave(p), p]));

/** Siglas de centro: lo que sigue es el nombre propio del centro (CEIP Cerro Castillo). */
const SIGLAS_CENTRO = new Set(['CEIP', 'IES', 'CPR', 'CEPER', 'CEE', 'CEI', 'EI', 'CP', 'IESO', 'CDP', 'CEA']);

/** Santoral: el token siguiente se capitaliza (San Blas, Santa Fe, Virgen de Gracia). */
const SANTORAL = new Set(['SAN', 'SANTA', 'SANTO', 'VIRGEN', 'CRISTO', 'STA', 'STO', 'NTRA', 'SRA']);

/** Locuciones institucionales con caja propia (se aplican al final, sin distinguir caja). */
const LOCUCIONES: Array<[RegExp, string]> = [
  [/\bpolic[ií]a local\b/gi, 'Policía Local'],
  [/\bguardia civil\b/gi, 'Guardia Civil'],
  [/\bprotecci[oó]n civil\b/gi, 'Protección Civil'],
  [/\bjunta de andaluc[ií]a\b/gi, 'Junta de Andalucía'],
  [/\bcruz roja\b/gi, 'Cruz Roja'],
  [/\bseguridad social\b/gi, 'Seguridad Social'],
  [/\bservicio andaluz de salud\b/gi, 'Servicio Andaluz de Salud'],
  [/\bcasa consistorial\b/gi, 'Casa consistorial'],
];

/** Tokens de topónimos municipales (≥ 5 letras) que no sean comunes: se capitalizan. */
let topónimos: Map<string, string> | null = null;
function getToponimos(): Map<string, string> {
  if (topónimos) return topónimos;
  topónimos = new Map();
  for (const m of MUNICIPIOS_POR_CODIGO.values()) {
    for (const t of m.nombre.split(/[\s\-/,()]+/)) {
      const k = clave(t);
      if (t.length >= 5 && !VACIAS.has(k) && !COMUNES.has(k) && !topónimos.has(k)) {
        topónimos.set(k, capitalizar(t));
      }
    }
  }
  return topónimos;
}

/**
 * PREPROTA (DOP-2 a): ¿es `token` una palabra conocida del diccionario de caja
 * (vacías, comunes, propios o topónimo municipal)? Clave sin tildes ni caja.
 * Lo usa el desconcatenador para no partir palabras correctas del documento
 * («Escuela» → «Escue la»): solo separa una preposición pegada si lo que
 * queda delante es palabra conocida («Sierrade» → «Sierra de»).
 */
export function esPalabraConocida(token: string): boolean {
  const k = clave(token);
  return k.length > 0 && (VACIAS.has(k) || COMUNES.has(k) || PROPIOS.has(k) || getToponimos().has(k));
}

// ─── Caja ────────────────────────────────────────────────────────────────────

function cajaToken(token: string, cabeza: boolean, capitalizaSiempre: boolean): string {
  // Punto final suelto («ZALABI.», «NTRA.»): se separa y se vuelve a pegar.
  const conPuntoFinal = token.endsWith('.');
  const cuerpo = conPuntoFinal ? token.slice(0, -1) : token;
  const k = clave(cuerpo);
  // Siglas con puntos internos (A.T., C.E.I.P., S.A), siglas de lista e iniciales: intactas.
  if (cuerpo.includes('.') || cuerpo.includes("'") || SIGLAS.has(k) || cuerpo.length === 1) {
    return token.toUpperCase();
  }
  // Abreviatura corta con punto final (Ntra., Sra., Avda., Frco.): capitalizada (RAE).
  if (conPuntoFinal && cuerpo.length <= 4) return capitalizar(cuerpo) + '.';
  const sufijo = conPuntoFinal ? '.' : '';
  const propio = PROPIOS.get(k);
  if (propio) return propio + sufijo;
  if (VACIAS.has(k)) return (cabeza ? capitalizar(cuerpo) : cuerpo.toLowerCase()) + sufijo;
  // El original con tilde manda sobre el catálogo sin ella («ALBÁNCHEZ» → «Albánchez»).
  const conTilde = /[ÁÉÍÓÚÜ]/.test(cuerpo);
  const top = getToponimos().get(k);
  if (top) return (conTilde ? capitalizar(cuerpo) : top) + sufijo;
  if (capitalizaSiempre) return capitalizar(cuerpo) + sufijo;
  const comun = COMUNES.get(k);
  if (comun) return (cabeza ? capitalizar(comun) : comun) + sufijo;
  // Desconocido: ≥ 5 letras conserva la inicial (probable propio); ≤ 4 baja (DOP-3).
  if (cabeza || k.length >= 5) return capitalizar(cuerpo) + sufijo;
  return cuerpo.toLowerCase() + sufijo;
}

// ─── Caja mixta (CAJATIT, 1-oct-2026) ────────────────────────────────────────
// Norma `.ptel/NORMA_CAJA_NOMBRES_RAE.md` (RAE, Ortografía 2010 § 4.2.4.7-8): en medio
// de frase, preposiciones y conjunciones en minúscula; artículos en minúscula solo tras
// preposición. Medido sobre el corpus (1 641 nombres en caja mixta): 202 cambian.

const PREP_MIXTA = new Set(['de', 'del', 'a', 'al', 'en', 'con', 'por', 'para', 'sin', 'sobre', 'entre', 'desde', 'hasta', 'hacia', 'tras', 'contra']);
const CONJ_MIXTA = new Set(['y', 'e', 'o', 'u']);
const ART_MIXTA = new Set(['el', 'la', 'los', 'las']);
const PREP_ANTE_ART = new Set(['de', 'a', 'en', 'con', 'por', 'para', 'sin', 'sobre', 'entre', 'desde', 'hasta', 'hacia', 'tras', 'contra']);
/** Siglas que no se leen como palabra: «Ies» → «IES» (DOP-3). «Renfe», «Cecopal» se quedan. */
const SIGLAS_TITULO = new Set(['IES', 'CEIP', 'CPR', 'CEPER', 'CEE', 'CEI', 'IESO', 'CDP', 'CEA', 'BIC', 'SCA', 'EDAR', 'ETAP', 'EDAM', 'EBAR', 'ERAR', 'GLP', 'GNL', 'SL', 'SLU', 'SLL', 'SAU', 'SAT', 'AIE', 'CB', 'CT', 'BT', 'MT', 'AMPA']);
/** Palabra en tipo título: «De», «Y». Lo escrito en versales dentro de un nombre mixto no se toca (G1). */
const EN_TITULO = /^[A-ZÁÉÍÓÚÜÑ][a-záéíóúüñ]*$/;

/** Municipios del catálogo que empiezan por artículo, en minúscula y sin tildes («los gallardos»). */
let municipiosConArticulo: string[] | null = null;
function getMunicipiosConArticulo(): string[] {
  if (!municipiosConArticulo) {
    municipiosConArticulo = [...MUNICIPIOS_POR_CODIGO.values()]
      .map(m => clave(m.nombre).toLowerCase())
      .filter(n => /^(el|la|los|las) /.test(n));
  }
  return municipiosConArticulo;
}

function cajaMixta(nombre: string): string {
  const tokens = [...nombre.matchAll(TOKEN)];
  const cambios: Array<[number, number, string]> = [];
  let anteriorConPunto = false;
  tokens.forEach((m, j) => {
    const w = m[0];
    const off = m.index ?? 0;
    const lw = w.toLowerCase();
    const previo = j > 0 ? tokens[j - 1][0] : '';
    const trasPunto = !anteriorConPunto && /\.\s+$/.test(nombre.slice(0, off));
    const cabeza = j === 0 || trasPunto;
    anteriorConPunto = w.includes('.') && !(w.endsWith('.') && w.indexOf('.') === w.length - 1 && w.length > 5);
    if (!EN_TITULO.test(w)) return; // G1
    if (w.length >= 2 && SIGLAS_TITULO.has(w.toUpperCase())) { cambios.push([off, w.length, w.toUpperCase()]); return; }
    if (cabeza || lw === w) return;
    const sigue = nombre.slice(off + w.length);
    const esVacia = PREP_MIXTA.has(lw) || CONJ_MIXTA.has(lw);
    if (!esVacia && !ART_MIXTA.has(lw)) return;
    // G7: unida por guion a otra palabra («Al-Fakar», «Roalba-La Paz»).
    if (/^[-‐]/.test(sigue) || /[-‐]$/.test(nombre.slice(0, off))) return;
    if (esVacia) {
      // G2: letra suelta seguida de «:», «.», cifra o código («Y: 4126750», «Cl A Pp6»).
      if (w.length === 1 && (/^[:.\d]/.test(sigue) || /^\s*\S*\d/.test(sigue))) return;
      // G5: tras una preposición escrita en minúscula («Cueva de Sin Salida»).
      if (PREP_MIXTA.has(previo)) return;
      // G6: «Al» ante topónimo del catálogo («Complejo Deportivo Al Zalabí»).
      const siguiente = j + 1 < tokens.length ? tokens[j + 1][0] : '';
      if (lw === 'al' && siguiente && getToponimos().has(clave(siguiente))) return;
      cambios.push([off, w.length, lw]);
      return;
    }
    // Artículo: solo tras preposición; sin preposición delante se respeta («Charco La Tomilla»).
    const prev = previo.toLowerCase();
    if (!PREP_ANTE_ART.has(prev)) return;
    // G3: «de El X» — si el artículo no fuera del nombre, el documento habría escrito «del».
    if (lw === 'el' && prev === 'de') return;
    // G4: municipio del catálogo con artículo que cierra el nombre («Ayuntamiento de Los Gallardos»).
    const resto = clave(nombre.slice(off)).toLowerCase();
    if (getMunicipiosConArticulo().some(t => resto.startsWith(t) && /^(\s*$|\s*[),.;:\-–(])/.test(resto.slice(t.length)))) return;
    cambios.push([off, w.length, lw]);
  });
  let salida = nombre;
  for (const [p, l, s] of cambios.reverse()) salida = salida.slice(0, p) + s + salida.slice(p + l);
  return salida;
}

/**
 * Pasa un nombre a caja «frase con nombres propios».
 * - Sin letras, identificador de plantilla → intacto.
 * - Caja mixta → inicial de la primera palabra; vacías y artículos tras preposición a
 *   minúscula y siglas en tipo título a versales (CAJATIT; antes solo la inicial, DOP-4).
 * - Versales → frase por diccionarios (DOP-2 c′, DOP-3).
 */
export function aCajaRAE(nombre: string): string {
  if (!nombre || !LETRAS.test(nombre) || esIdentificador(nombre)) return nombre;

  if (!esVersales(nombre)) {
    // Caja mixta: inicial mayúscula en la primera letra y vacías a minúscula (CAJATIT).
    return cajaMixta(nombre.replace(LETRAS, c => c.toUpperCase()));
  }

  let indice = 0;
  let santoralPendiente = false;
  let trasSiglaCentro = false;
  let anteriorConPunto = false;
  let resultado = nombre.replace(TOKEN, (token, offset: number) => {
    // Cabeza de frase: primer token, o tras «. » de una palabra sin puntos (no de una sigla).
    const trasPunto = !anteriorConPunto && /\.\s+$/.test(nombre.slice(0, offset));
    const cabeza = indice === 0 || trasPunto;
    const k = clave(token.replace(/\./g, ''));
    const sigue = nombre.slice(offset + token.length);
    // CAJATIT (DOP-5 a): conjunción de una letra en medio de frase baja («SAN CAYETANO Y SAN
    // ANTÓN»), salvo ante «:», «.», guion o cifra («Y: 4126750»). La «A» suelta se queda: en
    // versales puede ser una letra («BLOQUE A»).
    const conjuncionSuelta = !cabeza && /^[YOEU]$/.test(token) && !/^[:.\d\-‐]/.test(sigue) && !/^\s*\S*\d/.test(sigue);
    const salida = sigue.charAt(0) === 'º'
      ? token // «Nº 107»
      : conjuncionSuelta ? token.toLowerCase() : cajaToken(token, cabeza, santoralPendiente || trasSiglaCentro);
    // Santoral: capitaliza el siguiente token no vacío («Virgen de la Cabeza»).
    if (SANTORAL.has(k)) santoralPendiente = true;
    else if (!VACIAS.has(k)) santoralPendiente = false;
    // Sigla de centro: el resto es el nombre propio del centro («CEIP Cerro Castillo»).
    if (SIGLAS_CENTRO.has(k)) trasSiglaCentro = true;
    anteriorConPunto = token.includes('.') && !(token.endsWith('.') && token.indexOf('.') === token.length - 1 && token.length > 5);
    indice++;
    return salida;
  });
  for (const [re, forma] of LOCUCIONES) resultado = resultado.replace(re, forma);
  return resultado;
}

/** ¿Difieren dos nombres más allá de la caja y las tildes? (DOP-6: `nombreOriginal` solo si sí). */
export function difierenSinCaja(a: string, b: string): boolean {
  return clave(a) !== clave(b);
}

/**
 * AYTOMUN (29-sep-2026): el nombre exactamente «Ayuntamiento» (tras la caja, con o sin punto
 * final) pasa a «Ayuntamiento de <nombre INE>» cuando el municipio de la fila resuelve a INE.
 * El nombre sale del catálogo INE (tildes y artículo: «Los Gallardos», «La Guardia de Jaén»),
 * no de la grafía de la fila («Benalua de las Villas»). Con calificativo («– sede Secopal»,
 * «(Casa de los Fuentes)»), sin municipio o con municipio sin INE → intacto.
 * Medido sobre el corpus (2 135 filas): 20 «Ayuntamiento» a secas, 19 con municipio resuelto.
 */
export function conMunicipioAyuntamiento(nombre: string, municipioFila?: string): string {
  if (!municipioFila || !/^ayuntamiento\.?$/i.test(nombre.trim())) return nombre;
  const ine = getCodigoINE(municipioFila);
  const oficial = ine ? getMunicipioPorCodigo(ine)?.nombre : undefined;
  return oficial ? `Ayuntamiento de ${oficial}` : nombre;
}

// ─── Letra que falta o sobra en la cabeza de un equipamiento (CANONIC, 5-oct-2026) ──
// Única pieza que corrige texto del documento. Solo actúa con contexto: el token dudoso es la
// cabeza de una entrada de ≥ 2 palabras del catálogo y el resto de la entrada viene detrás tal
// cual («ENTRO DE TRANSFORMACION» → «CENTRO DE TRANSFORMACION», «Bilblioteca Municipal»).
// Medido sobre el corpus (2 135 filas, `.ptel/p0_canonic_20261005/AUDIT_CANONIC.md`): 2 reposiciones,
// 0 falsas con catálogos de 20, 39 y 96 entradas. La tolerancia sin contexto quedó descartada
// (PLAYA~PLAZA, SANATORIO~TANATORIO, RESIDENCIAL~RESIDENCIA, PADUA~PADUL…).

const ENTRADAS_CANONICAS: string[][] = [
  'Centro de transformación', 'Centro de salud', 'Centro de día', 'Centro de educación', 'Centro Guadalinfo',
  'Centro social', 'Centro cívico', 'Centro cultural', 'Casa de la cultura', 'Casa consistorial',
  'Campo de fútbol', 'Pista polideportiva', 'Guardia Civil', 'Policía Local', 'Protección Civil',
  'Estación de servicio', 'Estación de bombeo', 'Estación de autobuses', 'Depósito de agua', 'Punto limpio',
  'Consultorio médico', 'Hogar del pensionista', 'Residencia de mayores', 'Escuela infantil', 'Colegio público',
  'Parque de bomberos', 'Piscina municipal', 'Biblioteca municipal', 'Cementerio municipal', 'Tanatorio municipal',
  'Pabellón municipal', 'Mercado municipal', 'Polideportivo municipal', 'Iglesia parroquial',
  'Instituto de educación secundaria', 'Subestación eléctrica', 'Antena de telefonía', 'Plaza de toros',
  'Salón de usos múltiples',
].map(e => e.split(' '));

/** Palabras correctas a una edición de una cabeza del catálogo: nunca se tocan. */
const NO_SON_ERRATA = new Set(['SANATORIO', 'PLAYA']);

const TOKEN_LETRAS = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+/g;

/** ¿Distan `a` y `b` exactamente una edición (una letra de más, de menos o cambiada)? */
function aUnaEdicion(a: string, b: string): boolean {
  if (a === b || Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1);
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/**
 * CANONIC (DOP-1 a recortada): repone la cabeza de un equipamiento cuando le falta, le sobra o
 * tiene cambiada una letra, solo si el resto de la entrada del catálogo viene detrás y el token
 * no es palabra conocida. Conserva la caja del token (versales, inicial o minúscula); tildes y
 * caja definitiva las pone después `aCajaRAE`. No se aplica a direcciones ni tipos (DOP-5 a).
 */
export function reponerLetraPorCatalogo(nombre: string): string {
  if (!nombre) return nombre;
  const tokens = [...nombre.matchAll(TOKEN_LETRAS)];
  const claves = tokens.map(m => clave(m[0]));
  for (let i = 0; i < tokens.length - 1; i++) {
    const k = claves[i];
    if (k.length < 4 || NO_SON_ERRATA.has(k) || esPalabraConocida(k)) continue;
    for (const entrada of ENTRADAS_CANONICAS) {
      const cabeza = clave(entrada[0]);
      if (!aUnaEdicion(k, cabeza) || k === cabeza + 'S' || k === cabeza + 'ES') continue;
      if (!entrada.slice(1).every((w, j) => claves[i + 1 + j] === clave(w))) continue;
      const t = tokens[i][0];
      const repuesta = t === t.toUpperCase()
        ? entrada[0].toUpperCase()
        : t[0] === t[0].toUpperCase() ? entrada[0] : entrada[0].toLowerCase();
      const pos = tokens[i].index ?? 0;
      return nombre.slice(0, pos) + repuesta + nombre.slice(pos + t.length);
    }
  }
  return nombre;
}

// ─── Caja de la dirección (DIRCAJA, 4-oct-2026) ──────────────────────────────
// Solo caja (y la tilde de diccionario en lo que viene en versales). Medido sobre el corpus
// (1 731 direcciones, `.ptel/p0_dircaja_20261004/AUDIT_DIRCAJA.md`): `aCajaRAE` tal cual subía
// la inicial de 111 Plus Codes, bajaba la letra de portal («27 A») y, en versales, trataba el
// nombre de la calle como nombre común («Calle agua»). Aquí:
// - S1 · lo pegado a cifra, «+», «_», «º», «S/N», «km» y «pk» se copia intacto.
// - S3 · tras el tipo de vía solo bajan «de» y «del»; el artículo, nunca («C/ La Cruz»).
// - En versales todo lleva inicial mayúscula (el nombre de la vía es propio) salvo
//   preposiciones, conjunciones y artículo tras preposición.
// - Abreviaturas («Avd.», «C/», «Nº», «Sn.») no se unifican.

const VIA_DIR = new Set(['CALLE', 'C', 'CL', 'AVENIDA', 'AVDA', 'AVD', 'AV', 'PLAZA', 'PZA', 'PL', 'PLZA', 'CAMINO', 'CNO', 'PASEO', 'CARRETERA', 'CTRA', 'CRTA', 'CR', 'PARAJE', 'POLIGONO', 'POL', 'BARRIO', 'BDA', 'BARRIADA', 'URBANIZACION', 'URB', 'TRAVESIA', 'TRAV', 'TR', 'RONDA', 'CUESTA', 'CTA', 'CALLEJON', 'GLORIETA', 'VEREDA', 'PAGO', 'ALAMEDA', 'PLAZUELA', 'PASAJE']);
const VIA_ABREV_DIR = new Set(['AV', 'CL', 'PL', 'CR', 'TR', 'C']);
const PREP_DIR = new Set(['DE', 'DEL', 'A', 'AL', 'EN', 'CON', 'POR', 'PARA', 'SIN', 'SOBRE', 'ENTRE', 'DESDE', 'HASTA', 'HACIA', 'TRAS', 'CONTRA']);
const CONJ_DIR = new Set(['Y', 'E', 'O', 'U']);
const ART_DIR = new Set(['EL', 'LA', 'LOS', 'LAS']);
/** Romanos de dos o más letras hasta XXXIX («N-IV», «Juan XXIII»). */
const ROMANO_DIR = /^(?=[IVX]{2,})X{0,3}(IX|IV|V?I{0,3})$/;
const PALABRA_SIGUE = /^\s+[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]{2,}/;

function claveToken(token: string): string {
  return clave(token.replace(/\./g, ''));
}

/** S1: token que se copia tal cual. */
function tokenIntactoDireccion(dir: string, token: string, off: number): boolean {
  const antes = dir.slice(0, off);
  const sigue = dir.slice(off + token.length);
  const k = claveToken(token);
  if (/[\d+_]$/.test(antes) || /^[\d+_]/.test(sigue) || /^[-‐]\s?\d/.test(sigue) || /\d[-‐]$/.test(antes) || /^[ºª°]/.test(sigue)) return true;
  if ((k === 'S' && /^\/N/i.test(sigue)) || (k === 'N' && /S\/$/i.test(antes))) return true;
  if (k === 'KM' || k === 'PK') return true;
  // Letra de portal o de bloque tras cifra («27 A», «2 A,»).
  return token.length === 1 && /\d\s$/.test(antes);
}

/** Caja de un token escrito en versales dentro de una dirección. */
function cajaTokenDireccion(dir: string, token: string, off: number, cabeza: boolean, previo: string): string {
  if (tokenIntactoDireccion(dir, token, off)) return token;
  const T = token.toUpperCase();
  const k = claveToken(T);
  if (ROMANO_DIR.test(T)) return T;
  const sigue = dir.slice(off + token.length);
  const cap = T.charAt(0) + T.slice(1).toLowerCase();
  if (T.length === 1) {
    // «Y», «O», «A» sueltas: bajan solo entre dos palabras («Junto a Rotonda»; «Bloque A» se queda).
    const entrePalabras = !cabeza && previo.length > 1 && PALABRA_SIGUE.test(sigue);
    return (CONJ_DIR.has(k) || k === 'A') && entrePalabras ? T.toLowerCase() : T;
  }
  if (!T.includes('.') && (PREP_DIR.has(k) || CONJ_DIR.has(k))) {
    if (cabeza) return cap;
    if (VIA_DIR.has(previo)) return k === 'DE' || k === 'DEL' ? T.toLowerCase() : cap; // S3
    return T.toLowerCase();
  }
  if (!T.includes('.') && ART_DIR.has(k)) {
    const trasPreposicion = PREP_DIR.has(previo) && previo !== 'DEL' && previo !== 'AL' && !(k === 'EL' && previo === 'DE');
    return !cabeza && trasPreposicion ? T.toLowerCase() : cap;
  }
  // Una o dos letras sin punto que no son abreviatura de vía: marca o código («BP», «GR 3420», «SN»).
  if (!T.includes('.') && T.replace(/\.$/, '').length <= 2 && !VIA_ABREV_DIR.has(k)) return T;
  // El topónimo del catálogo va por delante del diccionario de apellidos («Plaza de Cortes»).
  const top = T.includes('.') ? undefined : getToponimos().get(k);
  if (top) return /[ÁÉÍÓÚÜ]/.test(T) ? cap : top;
  return aCajaRAE(T); // cabeza de frase: inicial, tilde de diccionario, siglas intactas
}

/** ¿Están en versales (casi) todas las letras que no son de tokens intactos? («CALLE PASEO S/n»). */
function casiVersales(dir: string, tokens: RegExpMatchArray[]): boolean {
  let mayus = 0;
  let total = 0;
  for (const m of tokens) {
    if (tokenIntactoDireccion(dir, m[0], m.index ?? 0)) continue;
    for (const c of m[0]) {
      if (!LETRAS.test(c)) continue;
      total++;
      if (c === c.toUpperCase()) mayus++;
    }
  }
  return total > 0 && mayus / total >= 0.9;
}

/**
 * Pasa una dirección a caja RAE homogénea. Solo cambia la caja (y repone la tilde de
 * diccionario en lo que el documento trae en versales): la geocodificación no se entera
 * (`normalizeL1`, `normalizeL2` y `classifyAddressType` dan lo mismo antes y después).
 * Sin letras o identificador de plantilla → intacta.
 */
export function aCajaDireccion(direccion: string): string {
  if (!direccion || !LETRAS.test(direccion) || esIdentificador(direccion)) return direccion;
  const tokens = [...direccion.matchAll(TOKEN)];
  const claves = tokens.map(m => claveToken(m[0]));

  if (casiVersales(direccion, tokens)) {
    let salida = direccion;
    for (let j = tokens.length - 1; j >= 0; j--) {
      const off = tokens[j].index ?? 0;
      const nuevo = cajaTokenDireccion(direccion, tokens[j][0], off, j === 0, j > 0 ? claves[j - 1] : '');
      salida = salida.slice(0, off) + nuevo + salida.slice(off + tokens[j][0].length);
    }
    return salida;
  }

  // Caja mixta: la regla de los nombres (CAJATIT) y, encima, las salvaguardas de dirección.
  const mixta = aCajaRAE(direccion);
  if (mixta.length !== direccion.length) return direccion;
  let salida = mixta;
  const enVersales = (j: number) => {
    const t = tokens[j][0];
    const cuerpo = t.replace(/\.$/, ''); // el punto final no cuenta («CRISTO.»); los interiores sí («S.C.A»)
    return cuerpo.length >= 2 && !cuerpo.includes('.') && t === t.toUpperCase() && !tokenIntactoDireccion(direccion, t, tokens[j].index ?? 0);
  };
  const larga = (j: number) => tokens[j][0].replace(/\.$/, '').length >= 4 && !SIGLAS.has(claves[j]) && !ROMANO_DIR.test(tokens[j][0]);
  for (let j = tokens.length - 1; j >= 0; j--) {
    const t = tokens[j][0];
    const off = tokens[j].index ?? 0;
    const previo = j > 0 ? claves[j - 1] : '';
    let nuevo: string | null = null;
    if (tokenIntactoDireccion(direccion, t, off)) {
      nuevo = t; // S1
    } else if (j > 0 && VIA_DIR.has(previo) && (PREP_DIR.has(claves[j]) || CONJ_DIR.has(claves[j])) && claves[j] !== 'DE' && claves[j] !== 'DEL') {
      nuevo = t; // S3: «Calle En Medio»
    } else if (enVersales(j)) {
      // Tramo en versales dentro de una dirección mixta («… (GUADIX)», «LA GUARDIA DE JAEN»):
      // se trata como versales si el tramo contiene una palabra larga que no sea sigla.
      let a = j;
      let b = j;
      while (a > 0 && enVersales(a - 1)) a--;
      while (b < tokens.length - 1 && enVersales(b + 1)) b++;
      let hayLarga = false;
      for (let i = a; i <= b; i++) if (larga(i)) hayLarga = true;
      if (hayLarga && !SIGLAS.has(claves[j])) nuevo = cajaTokenDireccion(direccion, t, off, j === 0, previo);
    }
    if (nuevo !== null) salida = salida.slice(0, off) + nuevo + salida.slice(off + t.length);
  }
  return salida;
}
